import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, FormControl, IconButton, InputAdornment,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, Tooltip, Typography,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack, Refresh, Search, TuneRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as api from '../../../../services/subscriptionAPI';

// ── constants ─────────────────────────────────────────────────────────────────

const TH = { fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
  color: '#6B7280', letterSpacing: 0.5, bgcolor: '#F9FAFB', py: 1.5 };

const ALL_ACTIONS = [
  'plan_assigned', 'subscription_purchased', 'subscription_upgraded',
  'subscription_downgraded', 'subscription_renewed', 'subscription_cancelled',
  'subscription_suspended', 'subscription_reactivated', 'subscription_extended',
  'addon_purchased', 'topup_purchased', 'invoice_issued', 'invoice_voided',
  'refund_recorded', 'coupon_applied', 'token_adjusted', 'payment_recorded',
  'plan_published', 'plan_updated', 'plan_created', 'plan_archived', 'plan_duplicated',
  'addon_updated', 'coupon_created', 'coupon_updated', 'coupon_activated', 'coupon_deactivated',
  'verification_submitted', 'verification_approved', 'verification_rejected',
];

const ACTION_COLORS = {
  plan_assigned:             { bg: '#EDE9FE', color: '#5B21B6' },
  subscription_purchased:    { bg: '#DCFCE7', color: '#15803D' },
  subscription_upgraded:     { bg: '#DCFCE7', color: '#15803D' },
  subscription_renewed:      { bg: '#DCFCE7', color: '#15803D' },
  subscription_cancelled:    { bg: '#FEE2E2', color: '#B91C1C' },
  subscription_suspended:    { bg: '#FEF3C7', color: '#B45309' },
  subscription_reactivated:  { bg: '#DCFCE7', color: '#15803D' },
  subscription_extended:     { bg: '#DBEAFE', color: '#1D4ED8' },
  addon_purchased:           { bg: '#EDE9FE', color: '#5B21B6' },
  topup_purchased:           { bg: '#EDE9FE', color: '#5B21B6' },
  invoice_issued:            { bg: '#FEF3C7', color: '#B45309' },
  invoice_voided:            { bg: '#FEE2E2', color: '#B91C1C' },
  refund_recorded:           { bg: '#FEE2E2', color: '#B91C1C' },
  coupon_applied:            { bg: '#F0FDF4', color: '#15803D' },
  token_adjusted:            { bg: '#DBEAFE', color: '#1D4ED8' },
  payment_recorded:          { bg: '#F0FDF4', color: '#15803D' },
  verification_submitted:    { bg: '#FEF3C7', color: '#B45309' },
  verification_approved:     { bg: '#DCFCE7', color: '#15803D' },
  verification_rejected:     { bg: '#FEE2E2', color: '#B91C1C' },
};

const ACTOR_CHIP = {
  admin:    { bg: '#EDE9FE', color: '#5B21B6' },
  system:   { bg: '#F1F5F9', color: '#475569' },
  merchant: { bg: '#DCFCE7', color: '#15803D' },
};

const fmtAction = (a) =>
  (a || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

const fmtChanges = (vals) => {
  if (!vals || !Object.keys(vals).length) return null;
  return Object.entries(vals)
    .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
    .join('\n');
};

// ── main ─────────────────────────────────────────────────────────────────────

export default function AuditLogsPage() {
  const navigate = useNavigate();

  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(0);
  const [perPage, setPerPage]       = useState(25);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: perPage, sort_dir: 'desc' };
      if (search)       params.search      = search;
      if (actionFilter) params.action      = actionFilter;
      if (dateFrom)     params.date_from   = dateFrom;
      if (dateTo)       params.date_to     = dateTo;
      const r = await api.getAuditLogs(params);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, actionFilter, dateFrom, dateTo]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // reset to page 0 when filters change
  useEffect(() => { setPage(0); }, [search, actionFilter, dateFrom, dateTo, perPage]);

  return (
    <Box sx={{ p: 3 }}>

      {/* ── header ─────────────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start"
        mb={3} pb={2.5}
        sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
          mx: -3, px: 3, pt: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Tooltip title="Back to Subscriptions">
            <IconButton size="small" onClick={() => navigate(-1)}
              sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' } }}>
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h5" fontWeight={800} letterSpacing={-0.5} gutterBottom>
              Audit Logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subscription v2 activity trail
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadItems}
            sx={{ border: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'grey.50' }, mt: 0.5 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── filters ────────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2.5, overflow: 'hidden' }}>
        <Box display="flex" gap={1.5} px={2} py={1.5} alignItems="center" flexWrap="wrap"
          sx={{ bgcolor: 'grey.50' }}>
          <TuneRounded sx={{ fontSize: 18, color: 'text.disabled' }} />

          <TextField size="small" placeholder="Search merchant name / email"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, bgcolor: 'white', borderRadius: 1 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            )}} />

          <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
            <InputLabel>Action</InputLabel>
            <Select label="Action" value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}>
              <MenuItem value="">All actions</MenuItem>
              {ALL_ACTIONS.map((a) => (
                <MenuItem key={a} value={a}>{fmtAction(a)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField size="small" label="From" type="date"
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150, bgcolor: 'white' }} />

          <TextField size="small" label="To" type="date"
            value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150, bgcolor: 'white' }} />

          {(search || actionFilter || dateFrom || dateTo) && (
            <Button size="small" variant="text" color="inherit"
              onClick={() => { setSearch(''); setActionFilter(''); setDateFrom(''); setDateTo(''); }}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: 12 }}>
              Clear filters
            </Button>
          )}

          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── table ──────────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={TH}>Date / Time</TableCell>
                <TableCell sx={TH}>Merchant</TableCell>
                <TableCell sx={TH}>Actor</TableCell>
                <TableCell sx={TH}>Action</TableCell>
                <TableCell sx={TH}>Entity</TableCell>
                <TableCell sx={TH}>Changes</TableCell>
                <TableCell sx={TH}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No audit logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.map((row) => {
                const actionColor = ACTION_COLORS[row.action] || { bg: '#F3F4F6', color: '#374151' };
                const actorColor  = ACTOR_CHIP[row.actor_type] || ACTOR_CHIP.system;
                const changes     = fmtChanges(row.new_values);
                const entity      = row.entity_type
                  ? `${row.entity_type}${row.entity_id ? ` #${row.entity_id}` : ''}`
                  : '—';

                return (
                  <TableRow key={row.id} hover
                    sx={{ '&:last-child td': { border: 0 } }}>

                    <TableCell sx={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                      {fmtDate(row.created_at)}
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                        {row.merchant_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                        {row.merchant_email}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.4}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                          {row.actor_name}
                        </Typography>
                        <Chip label={row.actor_type} size="small"
                          sx={{ fontSize: 10, height: 18, fontWeight: 600,
                            bgcolor: actorColor.bg, color: actorColor.color,
                            borderRadius: '4px', width: 'fit-content' }} />
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip label={fmtAction(row.action)} size="small"
                        sx={{ fontSize: 11, height: 22, fontWeight: 600,
                          bgcolor: actionColor.bg, color: actionColor.color,
                          borderRadius: '6px' }} />
                    </TableCell>

                    <TableCell sx={{ fontSize: 12, color: '#6B7280', fontFamily: 'monospace' }}>
                      {entity}
                    </TableCell>

                    <TableCell sx={{ maxWidth: 220 }}>
                      {changes ? (
                        <Tooltip title={<pre style={{ margin: 0, fontSize: 11 }}>{changes}</pre>}
                          placement="left" arrow>
                          <Typography sx={{ fontSize: 11, color: '#6B7280', cursor: 'default',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 200 }}>
                            {changes.split('\n')[0]}{changes.split('\n').length > 1 ? ' …' : ''}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 11, color: '#D1D5DB' }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ maxWidth: 180 }}>
                      {row.notes ? (
                        <Tooltip title={row.notes} placement="left" arrow>
                          <Typography sx={{ fontSize: 12, color: '#374151', cursor: 'default',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: 160 }}>
                            {row.notes}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 11, color: '#D1D5DB' }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => setPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Paper>
    </Box>
  );
}

